/**
 * Location Form Data Transformation Utilities
 * Transforms client-side form data to match API schema expectations
 */

import { Branch, OpeningHours } from '@/types/branch.types';
import { BranchContact } from '@/types/branchContact.types';

/**
 * API Schema for Location Submission
 * Based on actual API documentation from Swagger
 */

// Individual location contact nested inside location
export interface ApiContactBranch {
  language: string;
  function: string;
  deputy: boolean; // Is this contact a deputy?
  salutation: string; // "Mr." | "Ms." | "Mrs." | "Dr." etc.
  first_name: string;
  last_name: string;
  email: string;
  mobile?: string;
  phone: string;
}

// Individual branch data structure expected by API
export interface ApiBranchData {
  // Basic Information
  language: string; // "de" | "en" | "fr"
  name: string; // Branch name
  country: string; // Full country name (e.g., "Switzerland", not "CH")

  // Address Information
  street: string;
  street_number: string;
  post_box?: string; // Optional PO Box
  postal_code: string;
  location: string; // City/location name
  canton?: string; // Canton abbreviation (e.g., "ZH")

  // Opening Hours - Simple string format
  opening_hours: string; // e.g., "Mon-Fri: 9:00-17:00"

  // Service Information
  service_availability_times?: string; // e.g., "24/7 Support Available"

  // Nested contacts
  contact_branches: ApiContactBranch[];
}

// Complete submission payload expected by API
export interface ApiBranchFormSubmissionPayload {
  token: string; // Validation token from email (64-character hex string)
  branches: ApiBranchData[];
}

/**
 * Format day name for display
 * @param day - Day key (monday, tuesday, etc.)
 * @returns Formatted day abbreviation
 */
function formatDayAbbreviation(day: string): string {
  const abbreviations: Record<string, string> = {
    monday: 'Mon',
    tuesday: 'Tue',
    wednesday: 'Wed',
    thursday: 'Thu',
    friday: 'Fri',
    saturday: 'Sat',
    sunday: 'Sun',
  };
  return abbreviations[day.toLowerCase()] || day;
}

/**
 * Transform client-side OpeningHours to simple string format
 * Converts complex nested structure to human-readable string like "Mon-Fri: 9:00-17:00"
 *
 * @param openingHours - Client-side opening hours object
 * @returns Simple string representation of opening hours
 */
export function transformOpeningHours(openingHours: OpeningHours): string {
  const days = [
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
    'sunday',
  ];
  const schedulesByTime: Map<string, string[]> = new Map();

  // Group days by their time schedules
  days.forEach(day => {
    const schedule = openingHours[day as keyof OpeningHours];
    if (schedule?.enabled) {
      const timeKey = `${schedule.workingStart || ''}-${schedule.workingEnd || ''}`;
      const existingDays = schedulesByTime.get(timeKey) || [];
      existingDays.push(formatDayAbbreviation(day));
      schedulesByTime.set(timeKey, existingDays);
    }
  });

  // Format as "Mon-Fri: 9:00-17:00" or similar
  const scheduleStrings: string[] = [];
  schedulesByTime.forEach((days, timeKey) => {
    const [start, end] = timeKey.split('-');
    if (days.length > 1) {
      // Consecutive days can be abbreviated
      scheduleStrings.push(
        `${days[0]}-${days[days.length - 1]}: ${start}-${end}`
      );
    } else {
      scheduleStrings.push(`${days[0]}: ${start}-${end}`);
    }
  });

  return scheduleStrings.join(', ') || 'Closed';
}

/**
 * Get full country name from country code
 * @param countryCode - ISO country code (e.g., "CH", "DE")
 * @returns Full country name (e.g., "Switzerland", "Germany")
 */
function getFullCountryName(countryCode: string): string {
  const countryNames: Record<string, string> = {
    CH: 'Switzerland',
    DE: 'Germany',
    FR: 'France',
    IT: 'Italy',
    AT: 'Austria',
    LI: 'Liechtenstein',
  };
  return countryNames[countryCode.toUpperCase()] || countryCode;
}

/**
 * Transform client-side BranchContact to API nested format
 * Contacts are nested inside branches in the API
 * @param contact - Client-side branch contact data
 * @returns API-formatted contact for nesting in branch
 */
export function transformBranchContactToApi(
  contact: BranchContact
): ApiContactBranch {
  return {
    language: contact.language,
    function: contact.function,
    deputy: contact.isRepresentative, // Rename: isRepresentative → deputy
    salutation: 'Mr.', // TODO: Add salutation field to client-side form
    first_name: contact.firstName,
    last_name: contact.lastName,
    email: contact.email,
    mobile: contact.mobile,
    phone: contact.phone,
  };
}

/**
 * Transform client-side Branch to API format with nested contacts
 * @param branch - Client-side branch data
 * @param allContacts - All contacts to filter for this branch
 * @returns API-formatted branch data with nested contacts
 */
export function transformBranchToApi(
  branch: Branch,
  allContacts: BranchContact[]
): ApiBranchData {
  // Find contacts assigned to this branch
  const branchContacts = allContacts.filter(contact =>
    contact.branchIds.includes(branch.id)
  );

  return {
    language: branch.language,
    name: branch.name,
    country: getFullCountryName(branch.country),
    street: branch.street,
    street_number: branch.number,
    post_box: branch.postalCode, // Optional PO Box field
    postal_code: branch.zipCode,
    location: branch.city,
    canton: branch.canton,
    opening_hours: transformOpeningHours(branch.openingHours),
    service_availability_times: undefined, // TODO: Add to client-side form if needed
    contact_branches: branchContacts.map(transformBranchContactToApi),
  };
}

/**
 * Transform complete form data to API submission format
 * @param token - Validation token (64-character hex string)
 * @param branches - Array of client-side branches
 * @param branchContacts - Array of client-side branch contacts
 * @returns Complete API submission payload
 */
export function transformBranchFormDataToApi(
  token: string,
  branches: Branch[],
  branchContacts: BranchContact[]
): ApiBranchFormSubmissionPayload {
  return {
    token,
    branches: branches.map(branch =>
      transformBranchToApi(branch, branchContacts)
    ),
  };
}

/**
 * Example usage:
 *
 * ```typescript
 * import { transformBranchFormDataToApi } from '@/lib/branchFormTransformers';
 *
 * const apiPayload = transformBranchFormDataToApi(
 *   'valid-token-123',
 *   branches,
 *   branchContacts
 * );
 *
 * // Send to API
 * await apiService.post('/branches/submit-form', apiPayload);
 * ```
 */
